import { createViewportSpatialIndex } from '../render/viewport_spatial_index.mjs';
import { normalizePlanQueryData, PLAN_QUERY_LIMITS } from './plan_query_data.mjs';
import { queryGeometryBounds, queryHitMatches, queryBoxMatches } from './plan_query_geometry.mjs';

const CONTEXT_FIELDS = ['projectToken', 'sheetId', 'view', 'epoch', 'modelRevision', 'geometryRevision', 'assetRevision'];
const REVISIONS = ['epoch', 'modelRevision', 'geometryRevision', 'assetRevision'];
const OWNER = /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/;
const check = (valid, message) => { if (!valid) throw new TypeError(`Plan query: ${message}`); };
const sameContext = (a, b) => a === b || Boolean(a && b && CONTEXT_FIELDS.every(key => a[key] === b[key]));
const around = (p, radius) => ({ minX: p.x - radius, minZ: p.z - radius, maxX: p.x + radius, maxZ: p.z + radius });
function validPoint(p) {
  check(p && [p.x, p.z].every(n => Number.isFinite(n) && Math.abs(n) <= PLAN_QUERY_LIMITS.coordinate), 'invalid query point');
}
function readContext(getContext) {
  const raw = getContext();
  if (raw == null) return null;
  const value = Object.fromEntries(CONTEXT_FIELDS.map(key => [key, raw[key]]));
  check(value.projectToken && typeof value.projectToken === 'object'
    && typeof value.sheetId === 'string' && value.sheetId.length > 0
    && typeof value.view === 'string' && value.view.length > 0
    && REVISIONS.every(key => Number.isSafeInteger(value[key]) && value[key] >= 0), 'invalid host context');
  return Object.freeze(value);
}
function checkOwner(owner) {
  check(typeof owner === 'string' && owner.length <= 128 && OWNER.test(owner), 'invalid owner');
}
function compile(data) {
  const layers = new Map(data.layers.map(layer => [layer.id, layer]));
  const active = item => item.visible && !item.locked && layers.get(item.layerId).visible && !layers.get(item.layerId).locked;
  const groups = {};
  try {
    for (const name of ['hits', 'boxes', 'snaps']) {
      const items = data[name].filter(active);
      const entries = items.map((item, index) => ({ id: String(index), bounds: name === 'hits'
        ? queryGeometryBounds(item.geometry) : name === 'boxes' ? item.bounds : around(item.point, 0) }));
      groups[name] = { items, index: createViewportSpatialIndex(entries) };
    }
  } catch (error) {
    for (const group of Object.values(groups)) group.index.dispose();
    throw error;
  }
  let maxCss = 0, maxMm = 0;
  for (const hit of groups.hits.items) {
    maxCss = Math.max(maxCss, hit.tolerance.cssPx);
    maxMm = Math.max(maxMm, hit.tolerance.minMm);
  }
  return { groups, maxCss, maxMm, recordCount: data.recordCount, characters: data.characters,
    dispose() { for (const group of Object.values(groups)) { group.index.dispose(); group.items.length = 0; } } };
}
function identity(owner, target) {
  return { owner, id: target.id, objectId: target.objectId, kind: target.kind, layerId: target.layerId,
    ...(target.featureId === undefined ? {} : { featureId: target.featureId }) };
}

/**
 * Host-internal G07.1 service. Caller supplies detached model proxies, never a scene mesh.
 * beginUpdate/commit are preparation tokens, NOT a public plugin capability.
 * Synchronize on every host invalidation; reads also check context before using the index.
 */
export function createPlanQueryService({ getContext } = {}) {
  check(typeof getContext === 'function', 'getContext is required');
  const owners = new Map(), results = new WeakMap();
  let context = null, disposed = false, sequence = 0, version = 0, builds = 0, releases = 0;
  const release = record => {
    if (record.index) { record.index.dispose(); releases++; }
    record.index = null; record.request = null;
  };
  function synchronizeContext() {
    if (disposed) return null;
    let next;
    try { next = readContext(getContext); }
    catch (error) {
      for (const record of owners.values()) release(record);
      context = null; version++;
      throw error;
    }
    if (!sameContext(context, next)) {
      for (const record of owners.values()) release(record);
      context = next; version++;
    }
    return context;
  }
  function beginUpdate(owner) {
    checkOwner(owner);
    if (!synchronizeContext()) return null;
    check(owners.has(owner) || owners.size < PLAN_QUERY_LIMITS.owners, 'owner budget');
    let record = owners.get(owner);
    if (!record) { record = { index: null, request: null }; owners.set(owner, record); }
    release(record); version++;
    const token = Object.freeze({ owner, requestId: ++sequence });
    record.request = token;
    return token;
  }
  function commit(token, source) {
    if (!synchronizeContext() || !token) return false;
    const record = owners.get(token.owner);
    if (!record || record.request !== token) return false;
    let next = null;
    try {
      const data = normalizePlanQueryData(source);
      let count = data.recordCount, characters = data.characters;
      for (const other of owners.values()) {
        count += other.index?.recordCount || 0; characters += other.index?.characters || 0;
      }
      check(count <= PLAN_QUERY_LIMITS.records && characters <= PLAN_QUERY_LIMITS.characters, 'combined data budget');
      next = compile(data);
      // Defensive recheck for host invalidation/reentrancy during preparation.
      if (!synchronizeContext() || owners.get(token.owner) !== record || record.request !== token) {
        next.dispose(); return false;
      }
      record.index = next; record.request = null; builds++; version++;
      return true;
    } catch (error) {
      next?.dispose();
      if (record.request === token) { release(record); version++; }
      throw error;
    }
  }
  function scope(options) {
    synchronizeContext();
    const selected = options.owners === undefined ? [...owners.keys()] : options.owners;
    check(Array.isArray(selected) && selected.length <= PLAN_QUERY_LIMITS.owners, 'invalid owner filter');
    selected.forEach(checkOwner);
    check(new Set(selected).size === selected.length, 'duplicate owner filter');
    const kinds = options.kinds === undefined ? null : options.kinds;
    check(kinds === null || Array.isArray(kinds) && kinds.length <= 256
      && kinds.every(k => typeof k === 'string' && k.length > 0 && k.length <= 256), 'invalid kind filter');
    return { selected, kinds: kinds && new Set(kinds),
      ready: !disposed && Boolean(context) && selected.length > 0 && selected.every(owner => Boolean(owners.get(owner)?.index)) };
  }
  function finish(ready, values, diagnostics) {
    const result = Object.freeze({ status: ready ? 'ready' : 'unavailable', ...values,
      diagnostics: Object.freeze(diagnostics) });
    if (ready) results.set(result, version);
    return result;
  }
  function query(options, collection, boundsFor, evaluate) {
    const selected = scope(options), found = [];
    const diagnostics = { visited: 0, candidates: 0 };
    if (selected.ready) selected.selected.forEach((owner, ownerOrder) => {
      const index = owners.get(owner).index, group = index.groups[collection];
      const result = group.index.query(boundsFor(index)); diagnostics.visited += result.visited;
      for (const id of result.ids) {
        const order = Number(id), item = group.items[order];
        if (selected.kinds && !selected.kinds.has(item.kind)) continue;
        diagnostics.candidates++;
        const extra = evaluate(item);
        if (extra) found.push({ ownerOrder, order, item, result: Object.freeze({ ...identity(owner, item), ...extra }) });
      }
    });
    return { ...selected, found, diagnostics };
  }
  function hitTest(point, options = {}) {
    validPoint(point);
    check(Number.isFinite(options.scale) && options.scale >= 1e-6 && options.scale <= 1e6, 'invalid camera scale');
    const selectedOptions = options.preferredOwner === undefined ? options : { ...options, owners: [options.preferredOwner] };
    const q = query(selectedOptions, 'hits', index => around(point,
      // A rotated box padded on both local axes can extend sqrt(2) times the tolerance.
      Math.SQRT2 * Math.max(index.maxMm, index.maxCss / options.scale)), item => {
      const t = item.tolerance;
      const radius = Math.min(t.maxMm ?? Infinity, Math.max(t.minMm, t.cssPx / options.scale));
      return queryHitMatches(item.geometry, point, radius) ? {} : null;
    });
    q.found.sort((a, b) => a.ownerOrder - b.ownerOrder || b.item.priority - a.item.priority
      || b.item.order - a.item.order || a.order - b.order);
    return finish(q.ready, { hits: Object.freeze(q.found.map(f => f.result)) }, q.diagnostics);
  }
  function boxSelect(bounds, options = {}) {
    check(bounds && [bounds.minX, bounds.minZ, bounds.maxX, bounds.maxZ].every(n => Number.isFinite(n)
      && Math.abs(n) <= PLAN_QUERY_LIMITS.coordinate) && bounds.minX <= bounds.maxX && bounds.minZ <= bounds.maxZ, 'invalid query bounds');
    const mode = options.mode ?? 'window';
    check(mode === 'window' || mode === 'crossing', 'invalid box mode');
    const q = query(options, 'boxes', () => bounds, item => queryBoxMatches(item.bounds, bounds, mode) ? {} : null);
    q.found.sort((a, b) => a.ownerOrder - b.ownerOrder || a.order - b.order);
    const seen = new Set(), items = [];
    for (const { result } of q.found) {
      const key = JSON.stringify([result.owner, result.kind, result.objectId]);
      if (!seen.has(key)) { seen.add(key); items.push(result); }
    }
    return finish(q.ready, { items: Object.freeze(items) }, q.diagnostics);
  }
  function nearestSnap(point, options = {}) {
    validPoint(point);
    const threshold = options.thresholdMm ?? 100;
    check(Number.isFinite(threshold) && threshold >= 0 && threshold <= PLAN_QUERY_LIMITS.coordinate, 'invalid snap threshold');
    const q = query(options, 'snaps', () => around(point, threshold), item => {
      const distance = Math.hypot(item.point.x - point.x, item.point.z - point.z);
      return distance <= threshold ? { point: item.point, distance, score: distance - item.priority } : null;
    });
    q.found.sort((a, b) => a.result.score - b.result.score || a.ownerOrder - b.ownerOrder || a.order - b.order);
    return finish(q.ready, { snap: q.found[0]?.result ?? null }, q.diagnostics);
  }
  return Object.freeze({ beginUpdate, commit, hitTest, boxSelect, nearestSnap, synchronizeContext,
    isCurrent(result) { synchronizeContext(); return !disposed && Boolean(context) && results.has(result) && results.get(result) === version; },
    removeOwner(owner) {
      synchronizeContext(); const record = owners.get(owner);
      if (!record) return false;
      release(record); owners.delete(owner); version++; return true;
    },
    status() {
      synchronizeContext();
      return Object.freeze({ disposed, builds, releases, owners: owners.size,
        readyOwners: [...owners.values()].filter(record => Boolean(record.index)).length,
        records: [...owners.values()].reduce((sum, record) => sum + (record.index?.recordCount || 0), 0) });
    },
    dispose() {
      if (disposed) return;
      for (const record of owners.values()) release(record);
      owners.clear(); context = null; disposed = true; version++;
    }
  });
}
